import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  accountToday,
  invalidation,
  parseViewState,
  periodQueryOf,
  prefetchNeighbouringPeriods,
  resolveViewPeriod,
  viewStateFromPreferences,
  withViewState,
  type EntityLevel,
  type IsoDate,
  type UserPreferences,
  type ViewState,
} from '@sl/core';
import { useApiClient } from '../api/ApiClientProvider';
import { useClock } from '../clock/ClockProvider';
import { useShell } from '../shell/shellContext';
import { periodChipName } from './chipName';
import { PERIOD_COPY, PERIOD_LABEL, servedHint, timeZoneNote } from './copy';
import { PeriodContext, type PeriodApi, type PeriodChoice } from './periodContext';
import { useServerPeriod } from './serverPeriod';

// The date range as state (T-037; ARCH §9.2, §9.3). One instance, mounted by the shell around the top bar
// and the screen, so the control and every number on the page read the same range (REQ-021 AC4).
//
// Where the range comes from, in order:
//   1. the URL   — `parseViewState(useSearchParams())`; the source of truth while the page is open, and what
//                  makes a link reproduce a view. It never throws: a nonsense parameter falls back and is
//                  reported in `warnings[]`, which we log once in development and never show a user.
//   2. the saved preferences (`getPreferences`) — they fill only what the URL leaves out, and are re-written
//                  whenever the user changes the range, so the next visit opens where this one ended (AC4).
//   3. the product's own defaults — Last 7 days, comparison on (AC1, AC3). Never the user's preferences:
//                  a link has to look the same to everyone who opens it.
//
// `today` is **`account.today` — the SESSION's day, not the device's** (D-589). On a demo account the two are
// the same value by construction (the demo server reads the same injected Clock, D-020, so `?demoNow=` still
// moves everything). On a live dataset they are not: the data's today is the pull's day, and resolving "Last
// 7 days" against a real clock that has moved on gives a seven-day header over a six-day answer, a "Today"
// chip for a day with nothing in it, and — on the 1st — a Spend tab reading a different month from the date
// button (BUG-141's whole family). The server clamps as belt and braces (D-585 … D-587); this is the model.
//
// The across-midnight memo went with it: there is nothing to memoise, and `account.today` arrives on the
// session read, which refetches on focus (`AppShell`), so a tab left open overnight picks the new day up.
//
// It is published on the context so screens resolve their periods against exactly the day the date button is
// showing. Where the server answers a DIFFERENT range from the one asked for, `meta.period` is what the
// header renders (D-588 / D-589) — see `serverPeriod.ts`.

/** The four parameters that mean "this URL already says which period it wants". */
const PERIOD_PARAMS = ['period', 'since', 'until', 'compare'] as const;

function urlCarriesPeriod(params: URLSearchParams): boolean {
  return PERIOD_PARAMS.some((name) => params.has(name));
}

function samePeriod(a: UserPreferences['period'], b: UserPreferences['period']): boolean {
  return (
    a.preset === b.preset && a.since === b.since && a.until === b.until && a.compare === b.compare
  );
}

function periodOf(view: ViewState): UserPreferences['period'] {
  return { preset: view.preset, since: view.since, until: view.until, compare: view.compare };
}

type ListPreference = NonNullable<UserPreferences['lists'][EntityLevel]>;

/** REQ-042 AC4 — what is remembered PER LEVEL: the column preset, the quick filters and the sort. */
function listOf(view: ViewState): ListPreference {
  return {
    columnPreset: view.columnPreset,
    filters: [...view.filters],
    sort: { ...view.sort },
  };
}

function sameList(a: ListPreference | undefined, b: ListPreference): boolean {
  return (
    a !== undefined &&
    a.columnPreset === b.columnPreset &&
    a.sort.key === b.sort.key &&
    a.sort.dir === b.sort.dir &&
    a.filters.length === b.filters.length &&
    a.filters.every((f, i) => f === b.filters[i])
  );
}

export function PeriodProvider({ children }: { children: ReactNode }) {
  const { account, queries, messages } = useShell();
  const client = useApiClient();
  const clock = useClock();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();

  // D-589 — the SESSION's day (`AccountInfo.today`, "in the account timezone, the anchor for presets"), never
  // `accountToday(clock.now(), tz)`. See the header note: on the demo the two are the same value; on a live
  // dataset "now" is the DATA's clock, and this is the one line that makes it so.
  const today: IsoDate = account.today;

  const preferences = useQuery(queries.preferences());
  const savedPreferences = preferences.data?.data;

  // Preferences fill ABSENT parameters only (`viewStateFromPreferences`), and never the level: that comes
  // from the URL, so a list keeps the level it was linked to.
  const urlLevel = useMemo(() => parseViewState(params, { today }).state.level, [params, today]);
  const fallback = useMemo(
    () => (savedPreferences ? viewStateFromPreferences(savedPreferences, urlLevel) : undefined),
    [savedPreferences, urlLevel],
  );
  const parsed = useMemo(
    () => parseViewState(params, { today, fallback }),
    [params, today, fallback],
  );
  const view = parsed.state;

  // A bad link opens the default view; it never shows the user an error (ARCH §9.2). In development the
  // warnings go to the console so a wrong link in our own code is visible.
  const warningStamp = parsed.warnings.map((w) => `${w.param}:${w.code}`).join(',');
  useEffect(() => {
    if (!import.meta.env.DEV || warningStamp === '') return;
    for (const warning of warningStamp.split(','))
      console.warn(`[view state] ignored ${warning} in the URL`);
  }, [warningStamp]);

  const resolved = useMemo(() => resolveViewPeriod(view, today), [view, today]);
  const periodQuery = useMemo(() => periodQueryOf(view, today), [view, today]);

  // D-589 / D-588 — the server's echo of the four dates, and only when it DIFFERS from the ask: with the
  // presets resolved against `account.today` the two agree everywhere except a link that carries a custom
  // range running past the data's last day, which the server answers with the range that exists. The header
  // then says what is on screen rather than what was asked for; the freshness label says why (REQ-283).
  // …and only on an account whose data CAN be clamped (D-626, T-306): the rule is keyed off the rebase
  // (D-586) and the demo dataset is unchanged (D-585), so a demo account's echo always equals its ask. See
  // `serverPeriod.ts` for why that is a correctness line and not a saving.
  const live = account.mode === 'live_view_only' || account.mode === 'live_editing';
  const echo = useServerPeriod(periodQuery, live);
  const served = useMemo(
    () =>
      echo !== null && (echo.since !== resolved.since || echo.until !== resolved.until)
        ? echo
        : null,
    [echo, resolved.since, resolved.until],
  );

  /**
   * **REQ-021 AC6 (D-704) — the same preset resolved against the DEVICE's own day**, which is what the chip
   * compares itself with before it is willing to keep a name that claims a day.
   *
   * It is the one place in this provider that reads a clock: `today` above is deliberately the SESSION's day
   * (D-589), and the question AC6 asks is precisely "would somebody reading their own calendar mean these
   * dates by this word?". The day is taken in the ACCOUNT's zone, because `account.today` is a day in that
   * zone and two days can only be compared in one calendar.
   *
   * **Live-gated, exactly as `useServerPeriod` is** (D-626): a served range can only differ from the ask on a
   * live dataset — the demo server reads the same injected `Clock` the app does (D-020), so `?demoNow=` moves
   * both and they are the same value by construction. Off the live path there is nothing to compare, and the
   * demo's chip is unchanged in every mode.
   */
  const deviceResolved = useMemo(() => {
    if (!live) return null;
    const deviceToday = accountToday(clock.now(), account.timezone);
    if (deviceToday === today) return null;
    return resolveViewPeriod(view, deviceToday);
  }, [live, clock, account.timezone, today, view]);

  const chipName = useMemo(
    () =>
      periodChipName({
        preset: view.preset,
        shown: { since: served?.since ?? resolved.since, until: served?.until ?? resolved.until },
        device: deviceResolved,
        presetLabel: PERIOD_LABEL[view.preset],
        day: (date) => messages.format(date, 'date.short'),
        locale: messages.locale,
      }),
    [view.preset, served, resolved.since, resolved.until, deviceResolved, messages],
  );

  // -- writing --------------------------------------------------------------------------------------------

  const savedRef = useRef(preferences.data);
  savedRef.current = preferences.data;

  /**
   * REQ-021 AC4 "persists across sessions" and REQ-042 AC4 "per level per user": every change is mirrored to
   * the saved preferences — the period once, and the column preset / quick filters / sort under the level
   * the change was made at, so switching to Ad sets and back finds the list as it was left.
   *
   * The cache is written FIRST and synchronously. `parseViewState` fills an ABSENT `filter` / `sort` /
   * `columns` from these preferences, so a cache still holding the old ones would put a chip the user has
   * just cleared straight back on screen for the frame between the click and the server's answer.
   */
  const mirror = useCallback(
    (next: ViewState) => {
      const saved = savedRef.current;
      const current = saved?.data;
      if (!current) return;
      const nextList = listOf(next);
      if (
        samePeriod(current.period, periodOf(next)) &&
        sameList(current.lists[next.level], nextList)
      )
        return;
      const body: UserPreferences = {
        ...current,
        period: periodOf(next),
        lists: { ...current.lists, [next.level]: nextList },
      };
      const key = queries.preferences().queryKey;
      queryClient.setQueryData(key, { ...saved, data: body });
      void (async () => {
        try {
          const response = await client.putPreferences(body);
          queryClient.setQueryData(key, response);
          await queryClient.invalidateQueries(invalidation.putPreferences(account.id));
        } catch (error) {
          // A view that cannot be remembered still applies. Nothing the user did failed, so nothing is
          // said; the cache goes back to what the server last confirmed and the URL keeps the view.
          queryClient.setQueryData(key, saved);
          if (import.meta.env.DEV) console.warn('[preferences] could not save the view', error);
        }
      })();
    },
    [client, queryClient, queries, account.id],
  );

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const viewRef = useRef(view);
  viewRef.current = view;

  const setView = useCallback<PeriodApi['setView']>(
    (next, options) => {
      const value = typeof next === 'function' ? next(viewRef.current) : next;
      // A period change replaces: flipping between Last 7 days and Last 30 days is looking, not travelling,
      // and Back should leave the screen rather than walk through every range the user tried.
      setParams(withViewState(paramsRef.current, value), { replace: !options?.push });
      mirror(value);
    },
    [setParams, mirror],
  );

  const setPeriod = useCallback(
    (choice: PeriodChoice) => {
      setView((current) => ({
        ...current,
        preset: choice.preset,
        since: choice.preset === 'custom' ? (choice.since ?? null) : null,
        until: choice.preset === 'custom' ? (choice.until ?? null) : null,
      }));
    },
    [setView],
  );

  const setCompare = useCallback(
    (on: boolean) => setView((current) => ({ ...current, compare: on })),
    [setView],
  );

  // REQ-021 AC4 "encoded in the URL": once the saved preferences have arrived, a URL that said nothing about
  // the period is rewritten to the range it is actually showing, so the address bar is never a lie and the
  // link can be shared. `withViewState` keeps `demoNow` and the other QA switches.
  const canonicalised = useRef(false);
  useEffect(() => {
    // T-503: the legacy trim link redirects to a preset with its own explicit range. Normalising
    // its old query in the same commit can overwrite the destination's focus and date parameters.
    if (pathname === '/spend/allocation' && params.get('sort') === 'least_efficient') return;
    if (canonicalised.current || !savedPreferences || urlCarriesPeriod(params)) return;
    canonicalised.current = true;
    const next = withViewState(params, view);
    if (next !== params.toString()) setParams(next, { replace: true });
  }, [savedPreferences, params, view, setParams, pathname]);

  // -- the ≤ 300 ms period switch (REQ-280 AC2) -------------------------------------------------------------
  // While the user reads one range, the ranges either side of it in the control are fetched during idle time.
  // `keepPreviousData` (already on every period-keyed factory) covers whatever the prefetch has not warmed.
  // It depends on the PRESET and the comparison, not on the list controls: re-sorting a table warms nothing
  // new, and a custom range has no neighbours at all (`neighbouringPresets('custom')` is empty).
  //
  // **BUG-155 — "while the user reads one range" means AFTER that range has answered** (T-321). The gate
  // below is the fix, and it is the whole of it: `requestIdleCallback` treats any gap in a busy first second
  // as idle, so on a cold load this effect fired BEFORE the first number and warmed two neighbouring presets
  // — four more whole-account reads (`getSummary` ×2 + `getTimeSeries` ×2, measured on the built demo:
  // `window.__slDemoCalls` read `getSummary: 3` at the moment `kpi.spent.value` painted) racing the one read
  // the screen is waiting for, on the one thread the demo server runs on. REQ-280 AC1 is the budget that
  // comes first: AC2's warm-up is an optimisation for the NEXT click and must never be in front of the first
  // number. So the neighbours are warmed once the range on screen has answered, which is also exactly what
  // the comment above has always claimed.
  //
  // `enabled: false` makes this an OBSERVER of the cache and never a ninth request — the same bargain
  // `serverPeriod.ts` strikes one screen up. The read it watches is Home's own `queries.summary`, which is
  // the read `prefetchNeighbouringPeriods` warms, so the screen the warm-up serves is the screen that opens
  // the gate. `isFetched`, not `status`: every period-keyed factory carries `placeholderData:
  // keepPreviousData`, so on a period CHANGE the status is `success` from the previous range's numbers while
  // the new ones are still coming — "answered" has to mean this range answered. A disabled observer is not
  // an active one, so it cannot make the query refetch on an invalidation, and v5's tracked properties mean
  // this provider re-renders only when that one flag moves.
  const currentRangeAnswered = useQuery({
    ...queries.summary(account.id, periodQuery),
    enabled: false,
  }).isFetched;
  const { preset, compare } = view;
  useEffect(() => {
    if (!currentRangeAnswered) return;
    const run = () => {
      void prefetchNeighbouringPeriods(
        queryClient,
        queries,
        account.id,
        { preset, compare },
        today,
      );
    };
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(run);
      return () => window.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(run, 200);
    return () => window.clearTimeout(handle);
  }, [queryClient, queries, account.id, preset, compare, today, currentRangeAnswered]);

  const value = useMemo<PeriodApi>(
    () => ({
      view,
      today,
      resolved,
      periodQuery,
      label: PERIOD_LABEL[view.preset],
      // REQ-021 AC6 — the closed chip names the day it is really showing (D-704). The MENU row keeps
      // `label`; nothing about the preset's identity, the URL or the saved preference moves.
      chipName,
      soFarExplainer: chipName === PERIOD_LABEL[view.preset] ? null : PERIOD_COPY.soFarExplainer,
      // The dates on screen are the SERVED ones whenever they differ (D-589): every sentence in the app that
      // names the range — the gallery's "Didn't run Sep 15 – 21", a KPI caption — then names what is shown.
      dates: messages.format(
        { since: served?.since ?? resolved.since, until: served?.until ?? resolved.until },
        'date.range',
      ),
      compareDates: messages.format(
        {
          since: served?.compareSince ?? resolved.compareSince,
          until: served?.compareUntil ?? resolved.compareUntil,
        },
        'date.range',
      ),
      served,
      datesAsked:
        served === null
          ? null
          : messages.format({ since: resolved.since, until: resolved.until }, 'date.range'),
      servedHint:
        served === null
          ? null
          : servedHint(
              messages.format({ since: resolved.since, until: resolved.until }, 'date.range'),
            ),
      timeZoneNote: timeZoneNote(account.timezone),
      setView,
      setPeriod,
      setCompare,
      preferences: { isPending: preferences.isPending, isError: preferences.isError },
      settling: preferences.isPending && !urlCarriesPeriod(params),
    }),
    [
      view,
      today,
      resolved,
      served,
      chipName,
      periodQuery,
      messages,
      account.timezone,
      setView,
      setPeriod,
      setCompare,
      preferences.isPending,
      preferences.isError,
      params,
    ],
  );

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}
